import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GarminConnect } from '../GarminConnect.tsx';
import { IntervalsConnect } from '../IntervalsConnect.tsx';
import { UploadRidesDialog } from '../UploadRidesDialog.tsx';
import type { SetupPath, SetupPathId } from '../../hooks/useSetup.ts';
import { MUTED, PRIMARY_BUTTON, SETUP_CARD } from './styles.ts';

/** Numbered steps per path (``setup.paths.<id>.steps.<n>``); 0 = none. */
const STEP_COUNT: Record<SetupPathId, number> = {
  garmin_connect: 0,
  health_via_garmin: 3,
  intervals: 3,
  health_via_zwift: 3,
  health_via_app: 3,
  health: 2,
  upload: 0,
};

const HEALTH_PATHS = new Set<SetupPathId>(['health_via_garmin', 'health_via_zwift', 'health_via_app', 'health']);

/**
 * One card per ``paths`` entry, in the server's order (§6.2).
 * ``embedConnect`` puts the Garmin / intervals.icu connect cards inside
 * (onboarding); Profile → Integrations shows those cards below instead.
 */
export function SetupPaths({
  paths,
  embedConnect = false,
}: Readonly<{ paths: readonly SetupPath[]; embedConnect?: boolean }>) {
  return (
    <ul className="flex flex-col gap-3">
      {paths.map((path) => (
        <li key={path.id}>
          <PathCard path={path} embedConnect={embedConnect} />
        </li>
      ))}
    </ul>
  );
}

function PathCard({ path, embedConnect }: Readonly<{ path: SetupPath; embedConnect: boolean }>) {
  const { t } = useTranslation();
  const steps = Array.from({ length: STEP_COUNT[path.id] }, (_, i) => t(`setup.paths.${path.id}.steps.${i}`));
  return (
    <section aria-label={t(`setup.paths.${path.id}.title`)} className={SETUP_CARD}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-(--color-text-primary)">{t(`setup.paths.${path.id}.title`)}</h3>
        <PathStatus path={path} />
      </div>
      {steps.length > 0 && (
        <ol className={`${MUTED} list-decimal pl-5 flex flex-col gap-1`}>
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
      <PathAction id={path.id} embedConnect={embedConnect} />
    </section>
  );
}

function PathStatus({ path }: Readonly<{ path: SetupPath }>) {
  const { t } = useTranslation();
  if (path.id === 'upload' || path.status === 'not_connected') return null;
  const text =
    path.rides > 0 ? t('setup.status.ridesIn', { count: path.rides }) : t('setup.status.waiting');
  return <output className="text-xs text-(--color-accent)">{text}</output>;
}

function PathAction({ id, embedConnect }: Readonly<{ id: SetupPathId; embedConnect: boolean }>) {
  const { t } = useTranslation();
  if (HEALTH_PATHS.has(id)) {
    return <p className="text-sm font-medium text-(--color-text-primary)">{t('setup.paths.openApp')}</p>;
  }
  if (id === 'upload') return <UploadAction />;
  if (!embedConnect) return <p className={MUTED}>{t('setup.paths.connectBelow')}</p>;
  return id === 'garmin_connect' ? <GarminConnect /> : <IntervalsConnect />;
}

function UploadAction() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <p className={MUTED}>{t('setup.paths.upload.body')}</p>
      <button type="button" onClick={() => setOpen(true)} className={`self-start ${PRIMARY_BUTTON}`}>
        {t('setup.paths.upload.button')}
      </button>
      {open && <UploadRidesDialog onClose={() => setOpen(false)} />}
    </>
  );
}
